import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Avatar,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  alpha,
  useTheme,
  Skeleton,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../apis';
import { User } from '../types';

const ProfileSkeleton = () => {
  const theme = useTheme();
  return (
    <Box 
      sx={{ 
        pt: '84px', 
        minHeight: '100vh', 
        bgcolor: theme.palette.background.default,
        pb: 4
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={200} height={48} />
        </Box>

        <Grid container spacing={3}>
          {/* Profile Overview Skeleton */}
          <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
            <Paper 
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: theme.shape.borderRadius * 2,
                boxShadow: theme.shadows[2],
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: theme.palette.background.paper,
                minHeight: 400
              }}
            >
              <Skeleton variant="circular" width={140} height={140} sx={{ mb: 3 }} />
              <Skeleton variant="text" width={180} height={32} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={120} height={24} sx={{ mb: 3 }} />
              <Skeleton variant="rectangular" width={160} height={48} />
            </Paper>
          </Grid>

          {/* Contact Information Skeleton */}
          <Grid item xs={12} md={8}>
            <Paper 
              sx={{ 
                p: 4,
                borderRadius: theme.shape.borderRadius * 2,
                boxShadow: theme.shadows[2],
                bgcolor: theme.palette.background.paper,
                minHeight: 400
              }}
            >
              <Skeleton variant="text" width={200} height={32} sx={{ mb: 3 }} />
              <List sx={{ py: 0 }}>
                {[...Array(4)].map((_, index) => (
                  <ListItem 
                    key={index}
                    sx={{ 
                      px: 3,
                      py: 2,
                      borderRadius: theme.shape.borderRadius,
                      mb: 1,
                    }}
                  >
                    <ListItemIcon>
                      <Skeleton variant="circular" width={24} height={24} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Skeleton variant="text" width={120} />}
                      secondary={<Skeleton variant="text" width={200} />}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

const Profile: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await authAPI.getUser();
        if (response.status === 'success' && response.data.user) {
          setUser(response.data.user);
        } else {
          setError('Failed to load user data');
        }
      } catch (err) {
        setError('An error occurred while fetching user data');
        console.error('Error fetching user data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleEditProfile = () => {
    navigate('/settings');
  };

  if (loading) {
    return <ProfileSkeleton />;
  }

  return (
    <Box 
      sx={{ 
        pt: '84px', 
        minHeight: '100vh', 
        bgcolor: theme.palette.background.default,
        pb: 4
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            Profile
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {error && (
            <Grid item xs={12}>
              <Alert 
                severity="error"
                sx={{ 
                  borderRadius: theme.shape.borderRadius,
                  boxShadow: theme.shadows[1]
                }}
              >
                {error}
              </Alert>
            </Grid>
          )}
          
          {/* Profile Overview */}
          <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
            <Paper 
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: theme.shape.borderRadius * 2,
                boxShadow: theme.shadows[2],
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: theme.palette.background.paper,
                minHeight: 400
              }}
            >
              <Avatar
                sx={{
                  width: 140,
                  height: 140,
                  mb: 3,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '3rem',
                  fontWeight: 600,
                  boxShadow: theme.shadows[3]
                }}
              >
                {user?.username?.charAt(0).toUpperCase()}
              </Avatar>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 600,
                  mb: 1,
                  color: theme.palette.text.primary
                }}
              >
                {user?.username}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  mb: 3,
                  textTransform: 'capitalize'
                }}
              >
                {user?.userType}
              </Typography>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEditProfile}
                sx={{
                  borderRadius: theme.shape.borderRadius,
                  py: 1.5,
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                    boxShadow: theme.shadows[4]
                  }
                }}
              >
                Edit Profile
              </Button>
            </Paper>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={8}>
            <Paper 
              sx={{ 
                p: 4,
                borderRadius: theme.shape.borderRadius * 2,
                boxShadow: theme.shadows[2],
                bgcolor: theme.palette.background.paper,
                minHeight: 400
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  mb: 3,
                  color: theme.palette.text.primary
                }}
              >
                Contact Information
              </Typography>
              <List sx={{ py: 0 }}>
                <ListItem 
                  sx={{ 
                    px: 3,
                    py: 2,
                    borderRadius: theme.shape.borderRadius,
                    mb: 1,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  <ListItemIcon>
                    <EmailIcon sx={{ color: theme.palette.primary.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Email
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1" color="text.primary">
                        {user?.email}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem 
                  sx={{ 
                    px: 3,
                    py: 2,
                    borderRadius: theme.shape.borderRadius,
                    mb: 1,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  <ListItemIcon>
                    <PhoneIcon sx={{ color: theme.palette.primary.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Phone
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1" color="text.primary">
                        {user?.phone}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem 
                  sx={{ 
                    px: 3,
                    py: 2,
                    borderRadius: theme.shape.borderRadius,
                    mb: 1,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  <ListItemIcon>
                    <BusinessIcon sx={{ color: theme.palette.primary.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Organization
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1" color="text.primary">
                        {user?.organization}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem 
                  sx={{ 
                    px: 3,
                    py: 2,
                    borderRadius: theme.shape.borderRadius,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  <ListItemIcon>
                    <PersonIcon sx={{ color: theme.palette.primary.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Preferred Language
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1" color="text.primary">
                        {user?.preferredLanguage}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Profile; 